import { Component, Suspense, lazy, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '../../../../components/ui';
import type { MeshViewerModalProps } from './MeshViewerModal';
import styles from './PlanOptions.module.scss';

/**
 * three.js is ~600 kB of the CRM bundle and only ever runs inside this modal,
 * so it is pulled in on first open rather than on every admin page load.
 */
const MeshViewerModal = lazy(() =>
  import('./MeshViewerModal').then((module) => ({ default: module.MeshViewerModal })),
);

type BoundaryProps = {
  readonly fallback: ReactNode;
  readonly children: ReactNode;
};

/**
 * Contains chunk-load and render failures from the viewer.
 *
 * After a redeploy the hashed chunk this build points at is gone, so the dynamic
 * import rejects. Without a boundary that rejection unmounts the whole patient
 * page; here it degrades to a dismissible message.
 */
class ViewerErrorBoundary extends Component<BoundaryProps, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

type ViewerShellProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly children: ReactNode;
};

function ViewerShell({ open, onClose, title, children }: ViewerShellProps) {
  const { t } = useTranslation();
  return (
    <Modal open={open} onClose={onClose} title={title} size="xl" closeAriaLabel={t('dialog.close')}>
      {children}
    </Modal>
  );
}

export function MeshViewerModalLazy(props: MeshViewerModalProps) {
  const { t } = useTranslation();

  return (
    <ViewerErrorBoundary
      fallback={
        <ViewerShell open={props.open} onClose={props.onClose} title={props.title}>
          <p className={styles.viewerError} role="alert" data-testid="viewer-chunk-error">
            {t('meshViewer.errorTitle')}
            {' — '}
            {t('meshViewer.errorGeneric')}
          </p>
        </ViewerShell>
      }
    >
      <Suspense
        fallback={
          <ViewerShell open={props.open} onClose={props.onClose} title={props.title}>
            <p className={styles.viewerFootnote} role="status">
              {t('meshViewer.loading')}
            </p>
          </ViewerShell>
        }
      >
        <MeshViewerModal {...props} />
      </Suspense>
    </ViewerErrorBoundary>
  );
}
